import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { COMPARE_PARTNERS_SEARCH_PARAM } from '../../../partner_comparison_details/partner_comparison_details'
import { DirectoryPartnerProfileDataFragment, usePartnerLazyQuery } from '../../../../generated/graphql'
import { LopContext } from '../../../../util/language_preference_provider/language_preference_provider'
import { TRAINING_EXPERIMENT_KEY } from '../../../../shared/experiment'
import { ExperimentsContext, Snackbar, SnackbarState } from '@amzn/aapn-react-components'
import { PartnerSummaryProps } from '../cards/partner_summary_card/partner_summary_card'
import { useTranslation } from 'react-i18next'
import { directoryStrings } from '../../directory_i18n_mappings'
import { getMetricsPublisherChainAsync } from '@amzn/aapn-front-end-metrics'
import { buildPartnerProfile } from '../../../../util/partner_helper'
import { comparedPartnersMetricOnUrlParamsHandler } from '../../../../util/partner_comparison_metrics_helper'

export const MAX_COMPARED_PARTNER_COUNT = 3
export const COMPARED_PARTNERS_LOCAL_STORAGE_KEY = 'comparedPartners'
export interface PartnerDetails extends Omit<PartnerSummaryProps, 'currentPageSize' | 'currentPageNumber' | 'cardIndex'> {
  currentPageSize?: number
  currentPageNumber?: number
  cardIndex?: number
}
interface ComparedPartnersInfo {
  selectedPartners: PartnerDetails[]
  addSelectedPartner: (partner: PartnerDetails) => void
  removeSelectedPartner: (partnerId: string) => void
  loading: boolean
}

export const ComparedPartnersContext = createContext<ComparedPartnersInfo>({
  selectedPartners: [],
  addSelectedPartner: () => [],
  removeSelectedPartner: () => [],
  loading: false,
})

// TODO: validate partnerId using TypeGuard after this issue is fixed: https://sim.amazon.com/issues/AAPN-8668
const isPartnerIdValid = (partnerId: string): boolean => {
  return partnerId.trim().startsWith('amzn1.ads1.ma1.')
}

const getInvalidPartnerIds = (selectedPartnerIdStrings: string[]): string[] => selectedPartnerIdStrings.filter(partnerId => !isPartnerIdValid(partnerId))

export const createInitialComparedPartnerIds = (searchString: URLSearchParams): string[] => {
  if (searchString.has(COMPARE_PARTNERS_SEARCH_PARAM)) {
    const comparedPartnerIds = searchString
      .get(COMPARE_PARTNERS_SEARCH_PARAM)
      ?.split(',')
      .slice(0, MAX_COMPARED_PARTNER_COUNT)
    return comparedPartnerIds ?? []
  }
  return []
}

const buildPartnerDetails = (
  partner: DirectoryPartnerProfileDataFragment,
  languageOfPrefSelection: LopContext,
  trainingExperimentKey: string,
): PartnerDetails => buildPartnerProfile(partner, languageOfPrefSelection, trainingExperimentKey)

export const ComparedPartnersContextProvider: React.FunctionComponent = (props: PropsWithChildren<{}>): JSX.Element => {
  const history = useHistory()
  const searchParams = new URLSearchParams(history.location.search)
  const [selectedComparedPartners, setSelectedComparedPartners] = useState<PartnerDetails[]>([])
  const languageOfPrefSelection = useContext(LopContext)
  const { getTreatment } = useContext(ExperimentsContext)
  const trainingExperimentKey = getTreatment(TRAINING_EXPERIMENT_KEY)
  const [snackbarState, setSnackbarState] = useState<SnackbarState>(SnackbarState.CLOSED)
  const { t } = useTranslation()
  const location = useLocation()

  const [getPartnerDetails, { error: errorGettingComparedPartners, loading }] = usePartnerLazyQuery()
  const validatePartnerCount = (newPartners: (PartnerDetails | PartnerSummaryProps)[]): boolean => newPartners.length <= MAX_COMPARED_PARTNER_COUNT

  const context = {
    selectedPartners: selectedComparedPartners,
    addSelectedPartner: (newPartner: PartnerDetails) => {
      if (validatePartnerCount([...selectedComparedPartners, newPartner])) {
        setSelectedComparedPartners(prev => [...prev, newPartner])
      } else {
        getMetricsPublisherChainAsync('ComparedPartnersContext').then(p => p.publishCounter('exceeding.compared.partners', 1))
      }
    },
    removeSelectedPartner: (partnerId: string) =>
      setSelectedComparedPartners(prev => {
        return prev.filter(prevPartner => prevPartner.id !== partnerId)
      }),
    loading: loading,
  }

  useEffect(() => {
    if (errorGettingComparedPartners) {
      setSnackbarState(SnackbarState.OPEN)
    }
  }, [setSnackbarState, errorGettingComparedPartners])

  useEffect(() => {
    const selectedPartnerIdStrings = createInitialComparedPartnerIds(searchParams)
    const invalidPartnerIds = getInvalidPartnerIds(selectedPartnerIdStrings)
    if (invalidPartnerIds.length) {
      setSnackbarState(SnackbarState.OPEN)
    } else {
      const validPartnerIdStrings: string[] = selectedPartnerIdStrings
      const saturateComparedPartnersData = (partnerIdsToCompare: string[]): Promise<void> =>
        Promise.all(partnerIdsToCompare.map(partnerId => getPartnerDetails({ variables: { id: partnerId } }))).then(partnerDataResult => {
          const partnerDetailsArray = partnerDataResult
            .map(partnerData => {
              if (location.pathname === '/partners/directory') {
                comparedPartnersMetricOnUrlParamsHandler(
                  partnerData.data?.partner?.id,
                  'PartnerDirectoryHomePage',
                  'url.directory.partnerComparison.compare.partnerId',
                )
              } else if (location.pathname === '/partners/directory/compare') {
                comparedPartnersMetricOnUrlParamsHandler(partnerData.data?.partner?.id, 'ComparePartnerDetails', 'url.partnerComparison.page.compare.partnerId')
              }
              return partnerData.data?.partner && buildPartnerDetails(partnerData.data?.partner, languageOfPrefSelection, trainingExperimentKey)
            })
            .filter((partner): partner is PartnerDetails => !!partner)
          setSelectedComparedPartners(partnerDetailsArray)
        })
      if (validPartnerIdStrings.length) {
        saturateComparedPartnersData(validPartnerIdStrings)
      } else {
        const partnerIdsFromSessionStorage = sessionStorage.getItem(COMPARED_PARTNERS_LOCAL_STORAGE_KEY)
        if (partnerIdsFromSessionStorage) {
          saturateComparedPartnersData(partnerIdsFromSessionStorage.split(','))
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (selectedComparedPartners.length) {
      const partnerIds = selectedComparedPartners.map(partner => partner.id).filter((partnerId): partnerId is string => !!partnerId)
      const partnerIdsStrings = partnerIds.join(',')
      sessionStorage.setItem(COMPARED_PARTNERS_LOCAL_STORAGE_KEY, partnerIdsStrings)
      searchParams.set(COMPARE_PARTNERS_SEARCH_PARAM, partnerIdsStrings)
    } else {
      searchParams.delete(COMPARE_PARTNERS_SEARCH_PARAM)
      sessionStorage.removeItem(COMPARED_PARTNERS_LOCAL_STORAGE_KEY)
    }
    history.replace({ ...history.location, search: searchParams.toString() })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedComparedPartners, history.location.pathname])

  return (
    <ComparedPartnersContext.Provider value={context}>
      {props.children}
      <Snackbar
        snackbarState={snackbarState}
        id={'compared-partners-snackbar'}
        type={'error'}
        onClose={() => {
          setSnackbarState(SnackbarState.DISMISSED)
        }}
        closeButtonLabel={t(directoryStrings.ERROR_DISMISS)}
      >
        {t(directoryStrings.PARTNER_COMPARISON_ERROR)}
      </Snackbar>
    </ComparedPartnersContext.Provider>
  )
}
